import { memo, useCallback } from "react";
import Color from "color";

import { getDescriptor } from "../reducers/jobSlice";
import { useJobStatus } from '../reducers/jobStatusSlice';
import { priorityIcon, usePriority } from "../reducers/prioritySlice";
import { useType } from "../reducers/typeSlice";
import { useJobCrews } from "../reducers/crewSlice";
import { useBroadcast } from "../common/hooks/broadcastHook";
import useClientConfig from "../common/hooks/clientConfigHook";

import { useProblems } from "../reducers/problemSlice";
import PropTypes from 'prop-types';
import Icon from "../common/components/Icon";
import { Button } from "react-bootstrap";
import { useSite } from "../reducers/siteSlice";
import { useLevel } from "../reducers/levelSlice";
import { buildColor, buildShadedColor } from "../common/utils/tableUtilities";
import { useMapZonesForPoint } from "../reducers/mapZoneSlice";
import { Col, Row } from "../common/components/Grid";
import { useTranslation } from "react-i18next";
import { useEvent } from "../reducers/eventSlice";
import { groupBy } from "../common/utils/arrayUtilities";
import ElaspedClock from "../common/components/ElapsedClock";

const MarkerButton = ({value, kind, onClick }) => {
    return <Button className={'p-0' + (value ? '' : ' fa-hover-show')} style={{ backgroundColor: 'transparent', color: 'black', border: 'none'}} onClick={onClick}><Icon variant={value || kind === 'thumbtack' ? 'solid' : 'regular'} kind={kind}></Icon></Button>
}

MarkerButton.propTypes = {
    value: PropTypes.bool,
    kind: PropTypes.string,
    onClick: PropTypes.func
}

const NewMarkerButton = ({value, kind, onClick }) => {
    const isDarkMode = false;
    let color = Color(isDarkMode ? '#efefef' : '#9d9d9d');
    if (value) {
        color = Color('#186ADB');
    }
    return <Button className="p-0" style={{ backgroundColor: 'transparent', color: color.hex(), border: 'none'}} onClick={onClick}><Icon variant={value || kind === 'thumbtack' ? 'solid' : 'regular'} kind={!value && kind === 'thumbtack' ? 'thumbtack-slash' : kind}></Icon></Button>
}
NewMarkerButton.propTypes = {
    value: PropTypes.bool,
    kind: PropTypes.string,
    onClick: PropTypes.func
}

const JobListEntry = memo(({ job, selected, showSites = true, showLevels = true, OnClick, created, SetCreated, updated, SetUpdated, watched, SetWatched, pinned, SetPinned }) => {
    const { t } = useTranslation();
    const broadcast = useBroadcast();
    const clientConfig = useClientConfig();
    const priority = usePriority(job.priority);
    const type = useType(job.site, job.level, job.type);
    const crews = useJobCrews(job.jobid);
    const status = useJobStatus(job.status);
    const site = useSite(job.site);
    const level = useLevel(job.site, job.level);
    const mapZones = useMapZonesForPoint(job);
    const event = useEvent(job.event);
    useProblems();
    const hasControls = SetCreated || SetUpdated || SetWatched || SetPinned;

    const locateJob = useCallback((event) => {
        event.stopPropagation();
        event.preventDefault();
        broadcast('locate', { latitude: job.latitude, longitude: job.longitude });
    }, [job, broadcast]);

    const priorityColor = buildColor(priority?.color);
    const typeColor = buildShadedColor(type?.color);
    const siteColor = buildShadedColor(site?.color);
    const levelColor = buildShadedColor(level?.color);
    const statusColor = clientConfig.enableJobStatus ? buildColor(status?.color) : null;
    const handleClick = useCallback(() => OnClick?.(job), [job, OnClick]);

    return <button className="my-1 sel_jobEntry text-start w-100 p-0" key={job.jobid} style={{ borderRadius: '8px', borderWidth: '1px', borderColor: '#e0e0e0', borderStyle: 'solid', backgroundColor: selected ? '#ededed' : 'transparent' }} onClick={handleClick}>
        <Row>
            <Col className="sel_jobEntryPriority p-2 d-flex" xs="auto" style={{width: '30px', fontWeight: 'bold', textAlign: 'center', borderTopLeftRadius: '8px', borderBottomLeftRadius: '8px', alignItems: 'center', ...priorityColor }}>
                <Icon literal={priorityIcon(priority)}></Icon>
            </Col>
            <Col xs className="d-flex flex-column w-100 p-2">
                <div className="d-flex flex-row flex-grow-1" style={{ gap: '8px' }}>
                    <div className="d-flex flex-row flex-wrap" style={{ gap: '8px' }}>
                        <div className="text-secondary sel_jobEntryID d-flex align-items-center" style={{ fontSize: '0.85em', fontWeight: 'bold' }}>
                            #{job.displayid}
                        </div>
                        <div className="p-2 sel_jobEntryType d-flex align-items-center flex-nowrap" style={{ borderRadius: '4px', gap: '8px', ...typeColor }}>
                            <Icon literal={type?.icon}></Icon>
                            <span className="flex-grow-1">{job.type}</span>
                        </div>
                        { clientConfig.enableSites && showSites && <div className="p-2 sel_jobEntrySite d-flex align-items-center" style={{ borderRadius: '4px', ...siteColor }}>
                            {job.site}
                        </div> }
                        { clientConfig.enableLevels && showLevels && <div className="p-2 sel_jobEntryLevel d-flex align-items-center" style={{ borderRadius: '4px', ...levelColor }}>
                            {job.level}
                        </div>}
                    </div>
                    <div className="d-flex flex-column flex-grow-1">
                        <div className="text-secondary text-end text-nowrap" style={{ fontSize: '0.8em' }}>
                            <ElaspedClock baseDate={job.lastupdate} description={t('updated')}></ElaspedClock>
                        </div>
                        <div className="text-secondary text-nowrap text-end" style={{ fontSize: '0.8em' }}>
                            <ElaspedClock baseDate={job.created} description={t('created')}></ElaspedClock>
                        </div>
                    </div>
                </div>
                <div className="d-flex flex-row flex-grow-1 align-items-center" style={{ gap: '8px' }}>
                    <div className="d-flex flex-row flex-grow-1" style={{ gap: '8px' }}>
                        <div className="font-weight-bold sel_jobEntryProblem">
                            {job.problem}
                        </div>
                        <div className="px-1">
                            &ndash;
                        </div>
                        <div className="flex-grow-1">
                            {getDescriptor(clientConfig.jobListDescriptor, job)}
                        </div>
                        <div className="sel_jobEntryCrew text-secondary" style={{ alignItems: 'center', fontSize: '0.8em' }}>
                            { crews?.length > 0 ? <>
                                {t('crew_other')}: {Object.values(groupBy(crews, 'icon')).map(crews => <span key={crews[0].icon} className="text-nowrap"><Icon literal={crews[0].icon}></Icon> {crews.length > 1 ? `x${crews.length}` : ''}</span>)}
                            </> : <>
                                &nbsp;
                            </> }
                        </div>
                    </div>
                    <div className="d-flex flex-row justify-content-end align-items-stretch">
                        <div>
                            <div className="p-2 sel_jobEntryStatus" style={{ borderRadius: '4px', ...statusColor }}>
                                {job.status}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="d-flex flex-row flex-grow-1 align-items-center">
                    <div className="d-flex flex-row flex-wrap flex-grow-1 align-items-center">
                        <button className="d-flex flex-row flex-wrap flex-grow-1 align-items-center" style={{ border: 'none', gap: '8px', backgroundColor: 'transparent' }} onClick={OnClick}>
                            <div className="sel_jobEntryLocation" style={{ fontWeight: 'lighter' }}>
                                <span style={{ paddingInlineEnd: '4px' }}><Icon variant="solid" kind="location-dot"></Icon></span>{job.location}
                            </div>
                            {mapZones?.length > 0 && <div className="sel_jobEntryZone" style={{ fontSize: '0.8em', fontWeight: 'lighter' }}>
                                <span style={{ paddingInlineEnd: '4px' }}><Icon variant="regular" kind="map"></Icon></span>{mapZones.map(z => z.name).join(', ')}
                            </div>}
                        </button>
                        { event && <div className="d-flex row-reverse text-secondary flex-grow-1" style={{ fontSize: '0.8em', fontWeight: 'lighter', gap: '6px', justifyContent: 'flex-end', alignItems: 'center' }}>
                            <Icon variant="regular" kind="calendar"></Icon>
                            {event?.name}
                        </div> }
                    </div>
                    <div className="d-flex flex-column" style={{ alignItems: 'flex-end', marginTop: 'auto' }}>
                        { hasControls ? <div className="py-1 pr-3 d-flex flex-row" style={{ background: selected ? '#dedede': '#efefef', paddingLeft: '20px', clipPath: 'polygon(20px 0, 100% 0, 100% 100%, 0 100%)', marginTop: '8px', marginLeft: '8px', marginRight: '-8px', marginBottom: '-8px', borderBottomRightRadius: '8px', gap: '8px' }} onClick={e => e.stopPropagation()}>
                            { SetPinned && <div>
                                <NewMarkerButton value={pinned} kind="thumbtack" onClick={() => SetPinned(job, !pinned)}></NewMarkerButton>
                            </div>}
                            { SetWatched && <div>
                                <NewMarkerButton value={watched} kind="eye" onClick={() => SetWatched(job, !watched)}></NewMarkerButton>
                            </div>}
                            { SetCreated && <div>
                                <NewMarkerButton value={created} kind="star" onClick={() => SetCreated(job, !created)}></NewMarkerButton>
                            </div>}
                            { SetUpdated && <div>
                                <NewMarkerButton value={updated} kind="flag" onClick={() => SetUpdated(job, !updated)}></NewMarkerButton>
                            </div>}
                        </div> : <div></div> }
                    </div>
                </div>
            </Col>
        </Row>
    </button>
})

JobListEntry.propTypes = {
    job: PropTypes.object.isRequired,
    mode: PropTypes.oneOf(['classic','new']).isRequired,
    selected: PropTypes.bool.isRequired,
    OnClick: PropTypes.func,
    SetCreated: PropTypes.func,
    SetUpdated: PropTypes.func,
    SetWatched: PropTypes.func,
    SetPinned: PropTypes.func
}

export default JobListEntry;